import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import { useAuth } from "../hooks/useAuth";

function friendlyError(err: unknown): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "Falsches Passwort.";
      case "auth/too-many-requests":
        return "Zu viele Versuche. Bitte kurz warten und erneut probieren.";
      default:
        return "Konto konnte nicht gelöscht werden. Bitte erneut versuchen.";
    }
  }
  return "Konto konnte nicht gelöscht werden. Bitte erneut versuchen.";
}

export function AccountDelete() {
  const { user, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await deleteAccount(password);
      navigate("/login");
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-24 pt-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm font-medium text-turf-700"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
          <path d="m15 6-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Zurück
      </button>

      <h1 className="mt-4 font-display text-[1.75rem] font-semibold tracking-tight text-flag-600">Konto löschen</h1>
      <p className="mt-1 text-sm text-ink-600">
        Dein Konto ({user?.email}) und dein Login werden dauerhaft gelöscht. Das kann nicht rückgängig gemacht werden.
      </p>

      <div className="card mt-5 rounded-2xl border border-flag-100 p-4">
        <p className="text-sm text-ink-600">
          Bereits eingereichte Problem-Meldungen bleiben aus Dokumentationsgründen für den Club-Betreiber sichtbar, werden
          aber nicht mehr deinem Konto zugeordnet, sobald dieses gelöscht ist.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div>
          <label htmlFor="confirm-password" className="mb-1.5 block text-sm font-medium text-ink-900">
            Passwort zur Bestätigung
          </label>
          <input
            id="confirm-password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-12 w-full rounded-xl border border-chalk-200 bg-white px-4 text-base outline-none focus:border-flag-600"
          />
        </div>

        <label className="flex items-start gap-2.5 text-sm text-ink-900">
          <input
            type="checkbox"
            checked={confirmChecked}
            onChange={(e) => setConfirmChecked(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0"
          />
          Mir ist bewusst, dass diese Aktion endgültig ist und mein Konto unwiderruflich gelöscht wird.
        </label>

        {error && (
          <p role="alert" className="rounded-lg bg-flag-100 px-3 py-2 text-sm text-flag-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !confirmChecked}
          className="h-12 w-full rounded-xl bg-flag-600 text-base font-semibold text-chalk-50 shadow-sm transition-transform active:scale-[0.985] disabled:opacity-50"
        >
          {submitting ? "Wird gelöscht…" : "Konto endgültig löschen"}
        </button>
      </form>
    </main>
  );
}
